import { EducationExperienceGraduateType } from '@flex-apis/recruiting';
import {
  ClickPopover,
  DatePicker,
  FieldBox,
  FormField,
  PrimitiveButton,
  Typography,
} from '@flex-design-system/fx';
import {
  ChevronDownMiniIcon,
  TodayIcon,
} from '@flex-design-system/legacy-flex-icons';
import { isBeforeDay } from '@flex-packages/date';
import { TimeV2 } from '@flex-packages/datetime-react/components';
import { Translation, useLanguage, useTranslation } from '@flex-packages/i18n';
import { trans } from '@flex-packages/i18n-base';
import { isNonNil } from '@flex-packages/utils';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { JobDescriptionFormState } from '../../../../models';

interface Props {
  currentIndex: number;
  required: boolean;
}

const GraduatedTypes: Array<Partial<EducationExperienceGraduateType>> = [
  EducationExperienceGraduateType.Graduated,
  EducationExperienceGraduateType.PlanToGraduate,
];

export function EducationGraduationYearMonthInputFormItem({
  currentIndex,
  required,
}: Props) {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext<JobDescriptionFormState>();
  const locale = useLanguage();

  const graduationStatus = useWatch({
    control,
    name: `educationalBackgrounds.${currentIndex}.graduationStatus`,
  });
  const requiredGraduationDate =
    required && graduationStatus && GraduatedTypes.includes(graduationStatus);

  return (
    <Controller<
      JobDescriptionFormState,
      `educationalBackgrounds.${typeof currentIndex}.graduationDate`
    >
      name={`educationalBackgrounds.${currentIndex}.graduationDate`}
      rules={{
        required:
          requiredGraduationDate &&
          t(
            trans(
              'recruiting.educational_background.graduation_date.edit.helper.required'
            )
          ),
        validate: graduationDate => {
          const admissionDate = getValues(
            `educationalBackgrounds.${currentIndex}.admissionDate`
          );

          if (
            isNonNil(graduationDate) &&
            isNonNil(admissionDate) &&
            isBeforeDay(graduationDate, admissionDate)
          ) {
            return t(
              trans(
                'recruiting.educational_background.graduation_date.edit.helper.validate'
              )
            );
          }
        },
      }}
      render={({
        field: { value, onChange, ...field },
        fieldState: { error },
      }) => {
        const graduationDate = value ?? null;

        return (
          <FormField
            required={requiredGraduationDate}
            state={error ? 'error' : 'default'}
            {...field}
          >
            <FormField.Label leftSlot={<TodayIcon />}>
              <Translation
                tKey={trans(
                  'recruiting.educational_background.graduation_date'
                )}
              />
            </FormField.Label>
            <DatePicker
              mode="single"
              selectedDate={graduationDate}
              onDateChange={onChange}
            >
              <ClickPopover>
                <ClickPopover.Trigger asChild>
                  <FormField.FieldBox asChild>
                    <PrimitiveButton css={{ width: '100%' }}>
                      <FieldBox.Content>
                        {graduationDate !== null ? (
                          <TimeV2
                            date={graduationDate}
                            presetName="year_month"
                            locale={locale}
                          />
                        ) : (
                          <Typography color="grayLight" asChild>
                            <Translation
                              tKey={trans(
                                'recruiting.educational_background.graduation_date.edit.helper.placeholder'
                              )}
                            />
                          </Typography>
                        )}
                      </FieldBox.Content>
                      <FieldBox.RightSlotRoot>
                        <ChevronDownMiniIcon size={22} color="grayLight" />
                      </FieldBox.RightSlotRoot>
                    </PrimitiveButton>
                  </FormField.FieldBox>
                </ClickPopover.Trigger>
                <ClickPopover.Content>
                  <DatePicker.Content css={{ paddingTop: '6px' }}>
                    <DatePicker.MonthInput />
                    <DatePicker.MonthController />
                    <DatePicker.MonthCalendar />
                  </DatePicker.Content>
                </ClickPopover.Content>
              </ClickPopover>
            </DatePicker>
            {error?.message && (
              <FormField.HelperText>{error.message}</FormField.HelperText>
            )}
          </FormField>
        );
      }}
    />
  );
}
